import React, { useRef, useMemo } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface StarryBackgroundProps {
  count?: number;
  depth?: number;
  size?: number;
  color?: string;
  twinkleSpeed?: number;
}

// Cores de estrelas reais para maior realismo
const STAR_COLORS = [
  '#FFFFFF', // Branco
  '#F8F7FF', // Branco azulado
  '#FFF4E8', // Branco amarelado
  '#FFE9D1', // Amarelo claro
  '#FFD2A1', // Amarelo
  '#FFA756', // Laranja
  '#FF8080', // Vermelho claro
  '#CAD7FF', // Azul claro
  '#AABFFF', // Azul
];

const StarryBackground: React.FC<StarryBackgroundProps> = ({
  count = 5000,
  depth = 300,
  size = 0.5,
  color = '#ffffff',
  twinkleSpeed = 0.01
}) => {
  const starsRef = useRef<THREE.Points>(null);
  const starsGeometryRef = useRef<THREE.BufferGeometry>(null);
  
  // Criar as estrelas com posições e cores aleatórias
  const [positions, sizes, originalSizes, colors] = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const originalSizes = new Float32Array(count);
    const colors = new Float32Array(count * 3);
    
    for (let i = 0; i < count; i++) {
      // Distribuição das estrelas em uma esfera, com mais concentração no centro
      const radius = depth * Math.pow(Math.random(), 0.5); // Distribuição não uniforme
      const theta = 2 * Math.PI * Math.random();
      const phi = Math.acos(2 * Math.random() - 1);
      
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = radius * Math.cos(phi);
      
      // Tamanhos variados para as estrelas, com algumas estrelas maiores
      const sizeRandom = Math.random();
      const starSize = sizeRandom * sizeRandom * size * 1.5; // Distribuição quadrática para mais estrelas pequenas
      sizes[i] = starSize;
      originalSizes[i] = starSize;
      
      // Cores variadas para as estrelas
      const colorIndex = Math.floor(Math.random() * STAR_COLORS.length);
      const starColor = new THREE.Color(STAR_COLORS[colorIndex]);
      colors[i * 3] = starColor.r;
      colors[i * 3 + 1] = starColor.g;
      colors[i * 3 + 2] = starColor.b;
    }
    
    return [positions, sizes, originalSizes, colors];
  }, [count, depth, size]);
  
  // Efeito de cintilação (twinkle)
  useFrame(() => {
    if (starsGeometryRef.current) {
      const sizesAttribute = starsGeometryRef.current.attributes.size as THREE.BufferAttribute;
      
      for (let i = 0; i < count; i++) {
        // Fazer as estrelas cintilar alterando seus tamanhos
        const pulseSpeed = Math.random() * twinkleSpeed;
        const newSize = originalSizes[i] * (1 + 0.3 * Math.sin(Date.now() * pulseSpeed));
        sizesAttribute.array[i] = newSize;
      }
      
      sizesAttribute.needsUpdate = true;
    }
  });
  
  return (
    <points ref={starsRef}>
      <bufferGeometry ref={starsGeometryRef}>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-size"
          count={sizes.length}
          array={sizes}
          itemSize={1}
        />
        <bufferAttribute
          attach="attributes-color"
          count={colors.length / 3}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={size}
        sizeAttenuation={true}
        vertexColors={true} // Usar cores por vértice em vez de uma cor única
        transparent={true}
        opacity={0.9}
        fog={false}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        map={useMemo(() => {
          // Criar uma textura de estrela mais realista
          const canvas = document.createElement('canvas');
          canvas.width = 64;
          canvas.height = 64;
          const context = canvas.getContext('2d');
          if (context) {
            // Gradiente principal para o brilho central
            const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32);
            gradient.addColorStop(0, 'rgba(255,255,255,1)');
            gradient.addColorStop(0.4, 'rgba(255,255,255,0.8)');
            gradient.addColorStop(0.8, 'rgba(255,255,255,0.2)');
            gradient.addColorStop(1, 'rgba(255,255,255,0)');
            context.fillStyle = gradient;
            context.fillRect(0, 0, 64, 64);
            
            // Adicionar brilho em cruz para efeito de cintilação
            context.globalCompositeOperation = 'lighter';
            context.globalAlpha = 0.4;
            context.beginPath();
            context.moveTo(32, 0);
            context.lineTo(32, 64);
            context.lineWidth = 2;
            context.strokeStyle = 'white';
            context.stroke();
            context.beginPath();
            context.moveTo(0, 32);
            context.lineTo(64, 32);
            context.lineWidth = 2;
            context.strokeStyle = 'white';
            context.stroke();
          }
          const texture = new THREE.CanvasTexture(canvas);
          texture.needsUpdate = true;
          return texture;
        }, [])}
      />
    </points>
  );
};

export default StarryBackground;
